import { useMemo } from "react"

import { useI18next } from "gatsby-plugin-react-i18next"
import styled, { css } from "styled-components"

import GeneralCard from "components/Cards/GeneralCard"
import InnerWrapper from "components/InnerWrapper"
import Callout from "components/TextElements/Callout"
import { KickerTextType } from "components/TextElements/Kicker"
import ZoomImageGatsby from "components/UI-Related/ZoomImageGatsby"
import colors from "styles/colors"
import text from "styles/text"
import { fmobile, fresponsive, ftablet } from "utils/fullyResponsive"
import { checkedLocaleLink } from "utils/routes"
import useMedia from "utils/useMedia"
import { IGatsbyImageData } from "gatsby-plugin-image"
import { PageProps } from "gatsby"

interface Props extends PageProps {
  allAnimationImages: Record<string, IGatsbyImageData | undefined>
}

export default function PeopleAchieve({ allAnimationImages }: Props) {
  const titleWidth = useMedia("517px", "31.145vw", "47.461vw", "89.333vw")
  const textWidth = useMedia("516px", "31.084vw", "33.984vw", "89.333vw")
  const imageWidth = useMedia("905px", "54.518vw", "81.445vw", "89.333vw")
  const imageHeight = useMedia("519px", "31.265vw", "29.883vw", "81.6vw")
  const imageBorderR = useMedia("20px", "1.205vw", "20px", "20px")
  const borderVal = useMedia(false, false, true, true)
  const flex = useMedia(false, false, true, false)
  const { t, language } = useI18next()
  const { ourPeople } = allAnimationImages

  const cardData: {
    kickerIcon: KickerTextType
    title: string
    text: string
    url: string
  }[] = useMemo(
    () => [
      {
        title: t("Deposits"),
        text: t(
          "Solutions for modernizing deposits, back-end core infrastructure and cloud-native migrations."
        ),
        kickerIcon: "neobanking",
        url: "/deposits/use-case/core-modernization/",
      },
      {
        title: t("Engagement Channels"),
        text: t(
          "Solutions to modernize digital banking, featuring AI-powered tools, self-service options, and digital engagement channels for seamless customer interactions."
        ),
        kickerIcon: "b2b",
        url: "/channels/use-case/digital-banking/",
      },
      {
        title: t("Payments & Cards"),
        text: t(
          "Comprehensive products and services for card issuing, payment hub and program management. Including customer support and dispute resolution."
        ),
        kickerIcon:  "growth",
        url: "/services/co-brand-debit/",
      },
      {
        title: t("Lending"),
        text: t(
          "Integrated support for card issuing, payment processing, credit, analytics and risk so your program can reach its goals."
        ),
        kickerIcon: "crypto",
        url: "/lending/buy-now-pay-later/",
      },
      {
        title: t("Risk"),
        text: t(
          "Advanced tools for payment risk management, instant verification, and comprehensive fraud operations support."
        ),
        kickerIcon: "embedded",
        url: "/risk/instant-verification/",
      },
      {
        title: t("Platform"),
        text: t(
          "A cloud-based, developer-friendly, and regulation-ready solution with open APIs, sandbox testing, and trusted partnerships to serve financial institutions, fintechs, and brands."
        ),
        kickerIcon: "investing",
        url: "/platform/who-we-serve/",
      },
    ],
    [t]
  )

  const allCards = useMemo(
    () =>
      cardData.map(card => {
        const localeLink = checkedLocaleLink(card.url, language)

        const cardInfo = {
          icon: card.kickerIcon,
          title: card.title,
          body: card.text,
          link: localeLink,
          border: borderVal,
        }

        return <GeneralCard key={`card_${card.title}`} info={cardInfo} />
      }),
    [borderVal, cardData, language]
  )

  return (
    <Wrapper>
      <InnerWrapper>
        <ImageWrapper>
          {ourPeople && (
            <ZoomImageGatsby
              src={ourPeople}
              alt={t("two men standing at a whiteboard")}
              width={imageWidth}
              height={imageHeight}
              borderRadius={imageBorderR}
            />
          )}
        </ImageWrapper>
        <Callout
          title={{
            content: t("What can we achieve together?"),
            width: titleWidth,
            size: useMedia(text.h3, text.h3, text.h4, text.h1Mobile),
          }}
          text={{
            content: t(
              "We've spent 20+ years tackling financial technology challenges with early-stage start ups, emerging companies and established brands to launch and scale innovative payment processing and digital banking programs. No matter how you want to invent the financial experience, we're with you on the journey, so you can think boldly and deliver big."
            ),
            width: textWidth,
            color: colors.blue200,
          }}
          kicker={{
            text: t("OUR PEOPLE") ?? "OUR PEOPLE",
            icon: "slices",
            color: colors.blue200,
          }}
          flex={flex}
        />
        <CardsSection>{allCards}</CardsSection>
      </InnerWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  background: ${colors.lightMode};
  will-change: transform;
  transform: translate3d(0, 0, 0, 0);
  padding: 9.036vw 0 0;
  overflow: hidden;

  ${fresponsive(css`
    padding: 150px 0 67px;
  `)}

  ${ftablet(css`
    padding: 150px 0 150px;
  `)}

  ${fmobile(css`
    padding: 60px 20px 80px 20px;
  `)}
`
const ImageWrapper = styled.div`
  position: absolute;
  z-index: -1;

  ${fresponsive(css`
    top: -100px;
    left: 536px;
  `)}

  ${ftablet(css`
    position: relative;
    top: auto;
    left: auto;
    margin-bottom: 80px;
  `)}

  ${fmobile(css`
    position: relative;
    left: 0;
    margin-bottom: 40px;
  `)}
`

const CardsSection = styled.div`
  display: grid;
  position: relative;
  width: 100%;

  ${fresponsive(css`
    grid-template-columns: 1fr 1fr 1fr;
    margin-top: 189px;
    grid-column-gap: 60px;
    grid-template-rows: 360px;
    grid-row-gap: 80px;
  `)}

  ${ftablet(css`
    grid-template-columns: 1fr 1fr;
    margin-top: 80px;
    grid-column-gap: 20px;
    grid-template-rows: 360px;
    grid-row-gap: 40px;
  `)}

  ${fmobile(css`
    display: flex;
    flex-direction: column;
    gap: 20px;
  `)}
`
